import { useQuery } from '@tanstack/react-query'
import { Link, useBlocker } from '@tanstack/react-router'
import { ArrowLeft, Film, MapPinned, Play, Settings2, TreePine } from 'lucide-react'
import { useState } from 'react'

import { stageDetailsQuery, stageQuery, stagesQuery, type SceneryItem } from '@/api/stages'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { DetailsTab } from './DetailsTab'
import { PlacesTab } from './PlacesTab'
import { PreviewDialog } from './PreviewDialog'
import { SceneryTab } from './SceneryTab'
import { useSceneryEditor } from './useSceneryEditor'
import { stagePicture, worldLabel } from './words'

/**
 * `/backgrounds/<uid>` — one background: **Places** (what the studio knows about the picture,
 * with "Edit places" into the stage annotator), **Scenery** (the living things in the
 * background, laid out by hand or left automatic) and **Details** (name, world, used in stories,
 * notes). "Try it" in the header plays the place with a puppet walking across — unsaved scenery
 * included. Leaving with unsaved scenery asks first.
 */

export const BACKGROUND_TABS = ['places', 'scenery', 'details'] as const
export type BackgroundTab = (typeof BACKGROUND_TABS)[number]

export interface BackgroundEditorProps {
  uid: string
  tab?: BackgroundTab
  onTabChange?(tab: BackgroundTab): void
}

export function BackgroundEditor({ uid, tab = 'places', onTabChange }: BackgroundEditorProps) {
  // The stage's own row (`GET /api/stages/{uid}`), else its row in the list the grid already read.
  const one = useQuery(stageQuery(uid))
  const list = useQuery({ ...stagesQuery({}), enabled: one.isError })
  const details = useQuery(stageDetailsQuery(uid))
  const row = one.data?.item ?? list.data?.items.find((r) => r.uid === uid) ?? null
  const scenery = useSceneryEditor(uid)
  const [preview, setPreview] = useState<{ title: string; description: string; scenery?: SceneryItem[]; withWalker: boolean } | null>(null)

  const name = details.data?.name || row?.name || prettyUid(uid)
  const world = details.data?.world ?? row?.world ?? ''
  const enabled = details.data?.enabled ?? row?.enabled ?? true
  const picture = stagePicture(uid, row)
  const live = row?.kind === 'live' || uid.startsWith('live:')

  // Unsaved scenery: ask before leaving the page (switching tabs keeps it).
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => scenery.dirty && current.pathname !== next.pathname,
    enableBeforeUnload: () => scenery.dirty,
    withResolver: true,
  })

  const unsaved = scenery.dirty ? (scenery.draft.items ?? undefined) : undefined
  const tryIt = () =>
    setPreview({
      title: `Try “${name}”`,
      description: unsaved ? 'Someone walks across the place — with your scenery changes, not saved yet.' : 'Someone walks across the place, so you can see how big everything looks.',
      scenery: unsaved,
      withWalker: true,
    })
  const playScenery = () =>
    setPreview({
      title: 'Scenery preview',
      description: scenery.dirty ? 'Your scenery as it is now — not saved yet.' : scenery.mode === 'custom' ? 'Your saved scenery.' : 'What the studio picks by itself for this place.',
      scenery: unsaved,
      withWalker: false,
    })

  if (details.isError && one.isError && (list.isSuccess || list.isError) && !row) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-start gap-4 p-8" role="alert" data-testid="background-missing">
        <p className="text-lg text-ink">This background could not be opened.</p>
        <p className="text-sm text-ink-2">It may have been removed from the picture library.</p>
        <Button asChild variant="secondary">
          <Link to="/backgrounds">All backgrounds</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8" data-testid="background-editor">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/backgrounds">
            <ArrowLeft aria-hidden />
            All backgrounds
          </Link>
        </Button>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex min-w-0 max-w-3xl flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {world ? <Badge tone="neutral">{worldLabel(world)}</Badge> : null}
            {live ? (
              <Badge tone="info">
                <Film className="size-3" aria-hidden />
                Moving picture
              </Badge>
            ) : null}
            {!enabled ? <Badge tone="warn">Off — stories won’t pick it</Badge> : null}
          </div>
          <h1 className="truncate text-3xl font-semibold tracking-tight text-ink" title={name}>
            {name}
          </h1>
        </div>
        <Button variant="primary" size="lg" onClick={tryIt} data-testid="try-it">
          <Play aria-hidden />
          Try it
        </Button>
      </header>

      <Tabs value={tab} onValueChange={(v) => onTabChange?.(v as BackgroundTab)}>
        <TabsList aria-label="Parts of this background">
          <TabsTrigger value="places" className="h-9 px-3.5" data-testid="tab-places">
            <MapPinned className="size-4" aria-hidden />
            Places
          </TabsTrigger>
          <TabsTrigger value="scenery" className="h-9 px-3.5" data-testid="tab-scenery">
            <TreePine className="size-4" aria-hidden />
            Scenery
            {scenery.dirty ? (
              <>
                <span className="ml-0.5 size-2 rounded-full bg-accent" aria-hidden />
                <span className="sr-only">(not saved yet)</span>
              </>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="details" className="h-9 px-3.5" data-testid="tab-details">
            <Settings2 className="size-4" aria-hidden />
            Details
          </TabsTrigger>
        </TabsList>
        <TabsContent value="places" className="pt-4">
          {tab === 'places' ? <PlacesTab uid={uid} row={row} picture={picture} /> : null}
        </TabsContent>
        <TabsContent value="scenery" className="pt-4">
          {tab === 'scenery' ? <SceneryTab uid={uid} picture={picture} editor={scenery} onPreview={playScenery} /> : null}
        </TabsContent>
        <TabsContent value="details" className="pt-4">
          {tab === 'details' ? <DetailsTab uid={uid} row={row} /> : null}
        </TabsContent>
      </Tabs>

      <PreviewDialog
        open={preview !== null}
        onOpenChange={(o) => (!o ? setPreview(null) : undefined)}
        uid={uid}
        title={preview?.title ?? ''}
        description={preview?.description ?? ''}
        scenery={preview?.scenery}
        withWalker={preview?.withWalker}
      />

      <Dialog open={blocker.status === 'blocked'} onOpenChange={(o) => (!o ? blocker.reset?.() : undefined)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Leave without saving the scenery?</DialogTitle>
            <DialogDescription>Your changes to this background’s scenery are not saved yet.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => blocker.reset?.()}>
              Stay
            </Button>
            <Button variant="danger" onClick={() => blocker.proceed?.()} data-testid="leave-anyway">
              Leave anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** `bg:nature/meadow_morning` → "Meadow morning" until the name arrives. */
function prettyUid(uid: string): string {
  const last = uid.split('/').pop() ?? uid
  const t = last.replace(/^[a-z]+:/, '').replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : uid
}
